import { useRef, useEffect, useState } from 'react';
import { useGLTF, useAnimations } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

export function Motion({ 
  position = [0, 0, 0], 
  scale = 1,
  rotation = [0, 0, 0] 
}) {
  const group = useRef();
  const { nodes, animations } = useGLTF('/models/motion.glb');
  const { actions, mixer } = useAnimations(animations, group);
  const [playingForward, setPlayingForward] = useState(true);

  useEffect(() => {
    // Get the first animation
    const animation = Object.values(actions)[0];
    
    if (animation) {
      animation.reset();
      animation.clampWhenFinished = true;  // We'll handle looping manually
      animation.timeScale = 1;             // Start playing forward
      animation.play();

      // Remove the default loop
      animation.setLoop(THREE.LoopOnce);
    }

    // Hide all meshes
    group.current?.traverse((object) => {
      if (object.isMesh) {
        object.visible = false;
      }
    });

    // Find and setup skeleton
    let skeleton;
    group.current?.traverse((object) => {
      if (object.isSkinnedMesh && object.skeleton) {
        skeleton = object.skeleton;
      } else if (object.isBone) {
        object.visible = true;
      }
    });

    if (skeleton) {
      const helper = new THREE.SkeletonHelper(group.current);
      helper.material.linewidth = 15;
      helper.material.color.set('#00a0ff');
      group.current.add(helper);
    }

    return () => {
        if (actions) {
          Object.values(actions).forEach(action => {
            if (action && action.stop) {
              action.stop();
            }
          });
        }
      };
    }, [actions, mixer, nodes]);
  useFrame(() => {
    const animation = Object.values(actions)[0];
    if (!animation) return;

    const time = animation.time;
    const duration = animation.getClip().duration;
    
    // Check if we've reached the end of the animation
    if (playingForward && time >= duration - 0.1) {
      animation.timeScale = -1;  // Start playing backwards
      setPlayingForward(false);
    }
    // Check if we've reached the beginning while playing backwards
    else if (!playingForward && time <= 0.1) {
      animation.timeScale = 1;   // Start playing forwards
      setPlayingForward(true);
    }
  });

  return (
    <group 
      ref={group} 
      dispose={null}
      position={position}
      scale={typeof scale === 'number' ? [scale, scale, scale] : scale}
      rotation={rotation}
    >
      <primitive object={nodes.Scene} />
    </group>
  );
}

// Preload model
useGLTF.preload('/models/motion.glb');